<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { useVideoPreviewFrames } from '@/composables/useVideoPreviewFrames';

const props = defineProps<{
  /** The <video> element to control. May be null before the element mounts. */
  video: HTMLVideoElement | null;
  /** Source URL forwarded to the frame extractor (same URL as the video src). */
  src: string;
}>();

// ---------------------------------------------------------------------------
// Playback state — kept in sync with the video element via event listeners
// ---------------------------------------------------------------------------

const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);

let removeListeners: (() => void) | null = null;

function attach(video: HTMLVideoElement): () => void {
  const onTime = () => { currentTime.value = video.currentTime; };
  const onDuration = () => { duration.value = isFinite(video.duration) ? video.duration : 0; };
  const onPlay = () => { isPlaying.value = true; };
  const onPause = () => { isPlaying.value = false; };
  const onEnded = () => { isPlaying.value = false; };

  video.addEventListener('timeupdate', onTime);
  video.addEventListener('durationchange', onDuration);
  video.addEventListener('loadedmetadata', onDuration);
  video.addEventListener('play', onPlay);
  video.addEventListener('pause', onPause);
  video.addEventListener('ended', onEnded);

  // Sync immediately in case the video is already loaded/playing.
  isPlaying.value = !video.paused;
  currentTime.value = video.currentTime;
  duration.value = isFinite(video.duration) ? video.duration : 0;

  return () => {
    video.removeEventListener('timeupdate', onTime);
    video.removeEventListener('durationchange', onDuration);
    video.removeEventListener('loadedmetadata', onDuration);
    video.removeEventListener('play', onPlay);
    video.removeEventListener('pause', onPause);
    video.removeEventListener('ended', onEnded);
  };
}

watch(
  () => props.video,
  (video) => {
    removeListeners?.();
    removeListeners = null;
    if (video) removeListeners = attach(video);
  },
  { immediate: true },
);

onUnmounted(() => removeListeners?.());

// ---------------------------------------------------------------------------
// Seek-preview state
// ---------------------------------------------------------------------------

const previewVisible = ref(false);
const previewX = ref(0);
const previewTime = ref(0);
const seekBarEl = ref<HTMLInputElement | null>(null);

const srcRef = computed(() => props.src);
const { isExtracting, isReady, triggerExtraction, getFrameAtTime } =
  useVideoPreviewFrames(srcRef);

const previewFrame = computed(() =>
  isReady.value ? getFrameAtTime(previewTime.value) : '',
);

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

function togglePlay() {
  const v = props.video;
  if (!v) return;
  v.paused ? v.play() : v.pause();
}

function onSeekInput(e: Event) {
  const v = props.video;
  if (!v) return;
  v.currentTime = (e.target as HTMLInputElement).valueAsNumber;
}

function onSeekBarEnter() {
  triggerExtraction();
}

function onSeekBarMove(e: MouseEvent) {
  const bar = seekBarEl.value;
  if (!bar || !duration.value) return;
  const rect = bar.getBoundingClientRect();
  const fraction = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
  previewTime.value = fraction * duration.value;
  previewX.value = e.clientX - rect.left;
  previewVisible.value = true;
}

function onSeekBarLeave() {
  previewVisible.value = false;
}

function formatTime(s: number): string {
  if (!isFinite(s) || s < 0) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}
</script>

<template>
  <div class="video-controls">
    <!-- Seek bar row with frame-preview tooltip -->
    <div class="seek-row">
      <div class="seek-container">
        <!-- Preview tooltip: shown while hovering the seek bar -->
        <div
          v-if="previewVisible"
          class="seek-preview"
          :style="{ '--preview-x': `${previewX}px` }"
        >
          <img
            v-if="previewFrame"
            :src="previewFrame"
            class="preview-frame"
            alt=""
          />
          <span
            v-else-if="isExtracting"
            class="preview-loading"
            aria-label="Loading preview"
          >…</span>
          <span class="preview-time-label">{{ formatTime(previewTime) }}</span>
        </div>

        <input
          ref="seekBarEl"
          type="range"
          class="seek-bar"
          :min="0"
          :max="duration || 0"
          step="0.01"
          :value="currentTime"
          @input="onSeekInput"
          @mouseenter="onSeekBarEnter"
          @mousemove="onSeekBarMove"
          @mouseleave="onSeekBarLeave"
          @click.stop
        />
      </div>
    </div>

    <!-- Play/pause + time display -->
    <div class="controls-row">
      <button
        class="play-pause"
        :aria-label="isPlaying ? 'Pause' : 'Play'"
        @click.stop="togglePlay"
      >
        {{ isPlaying ? '⏸' : '▶' }}
      </button>
      <span class="time-display">
        {{ formatTime(currentTime) }}&thinsp;/&thinsp;{{ formatTime(duration) }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.video-controls {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 10px 8px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  font-size: 0.75rem;
  user-select: none;
  // Prevent any touch events from propagating (e.g. closing the zoom overlay).
  touch-action: none;
}

// ---- Seek bar ---------------------------------------------------------------

.seek-row {
  width: 100%;
}

.seek-container {
  position: relative;
  width: 100%;
}

.seek-bar {
  width: 100%;
  cursor: pointer;
  accent-color: var(--accent, #7f7);
  display: block;
}

// ---- Preview tooltip --------------------------------------------------------

.seek-preview {
  position: absolute;
  // Horizontal centre tracks the cursor, clamped so the preview stays within
  // the seek-container (half of THUMB_W = 80 px).
  left: clamp(80px, var(--preview-x, 50%), calc(100% - 80px));
  bottom: calc(100% + 6px);
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  pointer-events: none;
  z-index: 1;
}

.preview-frame {
  width: 160px;
  height: auto;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  display: block;
}

.preview-loading {
  width: 160px;
  height: 90px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 2px;
  font-size: 1.2rem;
}

.preview-time-label {
  font-size: 0.7rem;
  background: rgba(0, 0, 0, 0.7);
  padding: 1px 4px;
  border-radius: 2px;
  white-space: nowrap;
}

// ---- Controls row -----------------------------------------------------------

.controls-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.play-pause {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 1rem;
  padding: 0 4px;
  flex-shrink: 0;
  line-height: 1;
}

.time-display {
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
